import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

function useNow(intervalMs) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function formatElapsed(totalSeconds) {
  const secs = Math.floor(parseFloat(totalSeconds) || 0);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}

function elapsedMinutes(totalSeconds) {
  return (parseFloat(totalSeconds) || 0) / 60;
}

function formatClock(date) {
  return date.toLocaleTimeString(undefined, { hour:'2-digit', minute:'2-digit', second:'2-digit', hour12: false });
}

function formatDate(date) {
  return date.toLocaleDateString(undefined, { weekday:'long', year:'numeric', month:'long', day:'numeric' });
}

export default function FloorPage() {
  const [logs, setLogs] = useState([]);
  const now = useNow(1000);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/time-logs/floor');
      setLogs(data);
    } catch { /* silently retry next poll */ }
  }, []);

  // Initial load
  useEffect(() => { load(); }, [load]);

  // Refresh data every 10 seconds
  useEffect(() => {
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f1117',
      color: '#f0f4f8',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 40px',
        borderBottom: '1px solid #1e2330',
        background: '#13161f',
        flexShrink: 0,
      }}>
        <img src="/logo.jpg" alt="Logo" style={{ height: 52, objectFit: 'contain' }} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 44, fontWeight: 700, letterSpacing: 2, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {formatClock(now)}
          </div>
          <div style={{ fontSize: 15, color: '#6b7280', marginTop: 4 }}>
            {formatDate(now)}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, padding: '32px 40px', overflowY: 'auto' }}>
        <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 16, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
          Active Operations
        </div>

        {logs.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#374151',
            fontSize: 22,
            marginTop: 80,
            fontWeight: 500,
          }}>
            No active operations right now
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e2330' }}>
                {['Job', 'Assembly', 'Operation', 'Time Spent', 'Estimated'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left',
                    padding: '10px 16px',
                    fontSize: 13,
                    color: '#6b7280',
                    fontWeight: 600,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const over = log.estimated_minutes &&
                  elapsedMinutes(log.total_seconds) > parseFloat(log.estimated_minutes);
                return (
                  <tr key={log.operation_id} style={{
                    background: over ? 'rgba(220,38,38,0.15)' : 'transparent',
                    borderBottom: '1px solid #1e2330',
                    transition: 'background 0.5s',
                  }}>
                    <td style={{ padding: '18px 16px', fontSize: 22, fontWeight: 700, color: over ? '#f87171' : '#f9fafb', fontVariantNumeric: 'tabular-nums' }}>
                      {log.job_number}
                    </td>
                    <td style={{ padding: '18px 16px', fontSize: 18, color: over ? '#fca5a5' : '#d1d5db' }}>
                      {log.assembly_name}
                    </td>
                    <td style={{ padding: '18px 16px', fontSize: 18, color: over ? '#fca5a5' : '#d1d5db' }}>
                      {log.op_code && (
                        <span style={{ fontFamily: 'monospace', fontSize: 14, color: over ? '#f87171' : '#60a5fa', marginRight: 8, fontWeight: 700 }}>
                          {log.op_code}
                        </span>
                      )}
                      {log.op_name}
                    </td>
                    <td style={{
                      padding: '18px 16px',
                      fontSize: 28,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: over ? '#f87171' : '#34d399',
                      letterSpacing: 1,
                    }}>
                      {formatElapsed(log.total_seconds)}
                    </td>
                    <td style={{ padding: '18px 16px', fontSize: 16, color: '#6b7280' }}>
                      {log.estimated_minutes ? `${log.estimated_minutes} min` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
